import React, { useEffect, useMemo, useState, useRef } from "react";
import client from "../api/client";
import "./salaryHistory.css";
import { useMasterPins } from "../context/PinContext";
import { getEmployeeIdForProtectedAction } from "../utils/masterPinActions";
import { logEmployeeActivity } from "../utils/activityLogger";
import { 
  FaMoneyBillWave, 
  FaCheckCircle, 
  FaClock, 
  FaHandHoldingUsd, 
  FaArrowUp, 
  FaArrowDown,
  FaCalendarAlt,
  FaChevronDown,
  FaEye,
  FaTimes,
  FaTrash
} from "react-icons/fa";

const SALARY_HISTORY_PAGE_SIZE = 50;

const formatCurrency = (value) => {
  const number = Number(value || 0);
  if (Number.isNaN(number)) return "₹ 0.00";
  return `₹ ${number.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
};

// Generate available months (last 24 months) grouped by year
const generateAvailableMonths = () => {
  const months = [];
  const now = new Date();
  for (let i = 23; i >= 0; i--) {
    const d = new Date(now);
    d.setMonth(now.getMonth() - i);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    months.push({
      value: `${year}-${month}`,
      label: d.toLocaleDateString("en-IN", { month: "short" }),
      fullLabel: d.toLocaleDateString("en-IN", { month: "long", year: "numeric" }),
      year,
      month,
    });
  }
  return months;
};

const isWithinMonthSelection = (dateValue, selectedMonths) => {
  if (!selectedMonths || selectedMonths.length === 0) return true;
  const rowDate = new Date(dateValue || 0);
  if (Number.isNaN(rowDate.getTime())) return false;
  const rowMonth = `${rowDate.getFullYear()}-${String(rowDate.getMonth() + 1).padStart(2, "0")}`;
  return selectedMonths.includes(rowMonth);
};

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "-";

const formatSalaryMonth = (row) => {
  if (!row) return "-";
  return row.salary_month || row.salary_year
    ? `${row.salary_month || "-"} / ${row.salary_year || "-"}`
    : "-";
};

const getTotalWorkingDays = (row) => {
  if (!row) return "-";
  const totalDays = row.total_days_in_month;
  if (totalDays !== undefined && totalDays !== null && totalDays !== "") {
    return totalDays;
  }

  if (
    row.attendance_total_working_days !== undefined &&
    row.attendance_total_working_days !== null &&
    row.attendance_total_working_days !== ""
  ) {
    return row.attendance_total_working_days;
  }

  const presentDays = Number(row.present_days || 0);
  const absentDays = Number(row.absent_days || 0);
  const calculated = presentDays + absentDays;
  return calculated > 0 ? calculated : "-";
};

const getPaymentStatus = (row) =>
  row?.payment_status || row?.action_type || "-";

const getPresentDays = (row) =>
  row?.present_days ?? row?.attendance_present_days ?? "-";

const getAbsentDays = (row) =>
  row?.absent_days ?? row?.attendance_absent_days ?? "-";

// Group months by year for calendar grid
const groupMonthsByYear = (months) => {
  const grouped = {};
  months.forEach((m) => {
    if (!grouped[m.year]) grouped[m.year] = [];
    grouped[m.year].push(m);
  });
  return Object.entries(grouped).map(([year, items]) => ({ year, items }));
};

export default function SalaryHistory() {
  const { verifyPinOverride } = useMasterPins();
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [salaryHistory, setSalaryHistory] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [availableMonths] = useState(generateAvailableMonths());
  
  // New state for dropdown picker
  const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState(new Date().getFullYear());
  const monthPickerRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (monthPickerRef.current && !monthPickerRef.current.contains(event.target)) {
        setIsMonthPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
  // Get unique years from available months for dropdown
  const availableYears = useMemo(() => {
    const years = new Set(availableMonths.map(m => m.year));
    return Array.from(years).sort();
  }, [availableMonths]);
  
  // Get months for the currently selected picker year
  const currentYearMonths = useMemo(() => {
    const months = [];
    for (let i = 0; i < 12; i++) {
      const value = `${pickerYear}-${String(i + 1).padStart(2, "0")}`;
      const label = new Date(pickerYear, i, 1).toLocaleDateString("en-IN", { month: "short" });
      const available = availableMonths.find(m => m.value === value);
      months.push({
        value,
        label,
        available: !!available,
        selected: selectedMonths.includes(value)
      });
    }
    return months;
  }, [pickerYear, availableMonths, selectedMonths]);
  
  const [historyPage, setHistoryPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [selectedSalaryDetail, setSelectedSalaryDetail] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [summary, setSummary] = useState({
    totalPayout: 0,
    totalPaid: 0,
    totalPending: 0,
    totalAdvance: 0,
    paidPercent: 0,
    pendingPercent: 0,
  });
  const employeeId = getEmployeeIdForProtectedAction();

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (isUnlocked) {
      fetchSalaryHistory(selectedEmployee);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUnlocked, selectedEmployee]);

  const fetchEmployees = async () => {
    try {
      const res = await client.get("/users");
      setEmployees(res.data || []);
    } catch (err) {
      console.error("Employees load error", err);
    }
  };

  const fetchSalaryHistory = async (empId = "") => {
    if (!isUnlocked) return;
    try {
      setLoading(true);
      const historyUrl = empId
        ? `/salaries/history?employee_id=${encodeURIComponent(empId)}`
        : "/salaries/history";

      const res = await client.get(historyUrl);
      const data = res.data || [];
      setSalaryHistory(data);

      const totalPayout = data.reduce((sum, s) => sum + (Number(s.base_salary) || 0), 0);
      const totalPaid = data
        .filter((s) => s.action_type === "Paid")
        .reduce((sum, s) => sum + (Number(s.base_salary) || 0), 0);
      const totalPending = data
        .filter((s) => s.action_type === "Pending")
        .reduce((sum, s) => sum + (Number(s.base_salary) || 0), 0);
      const totalAdvance = data.reduce((sum, s) => sum + (Number(s.advance_amount) || 0), 0);
      const paidPercent = totalPayout ? (totalPaid / totalPayout) * 100 : 0;
      const pendingPercent = totalPayout ? (totalPending / totalPayout) * 100 : 0;

      setSummary({ totalPayout, totalPaid, totalPending, totalAdvance, paidPercent, pendingPercent });

      const selectedEmployeeName =
        employees.find((e) => String(e.id) === String(empId))?.name || "";

      void logEmployeeActivity({
        targetEmployeeId: empId ? Number(empId) : null,
        targetEmployeeName: selectedEmployeeName || undefined,
        action: "Salary History Viewed",
        module: "Salary",
        description: selectedEmployeeName
          ? `Viewed salary history for ${selectedEmployeeName}.`
          : "Viewed salary history.",
      });
    } catch (err) {
      console.error("Salary history error", err);
      setSalaryHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeChange = (e) => {
    setSelectedEmployee(e.target.value);
  };

  const toggleMonth = (monthValue) => {
    setSelectedMonths((prev) =>
      prev.includes(monthValue)
        ? prev.filter((m) => m !== monthValue)
        : [...prev, monthValue]
    );
  };

  const handleUnlock = async (e) => {
    e.preventDefault();
    let isOverrideValid = false;
    try {
      isOverrideValid = Boolean(
        employeeId && (await verifyPinOverride(employeeId, "salary_history", pin))
      );
    } catch (error) {
      console.error("Salary history PIN verification failed:", error);
    }

    if (isOverrideValid) {
      const selectedEmployeeName =
        employees.find((e) => String(e.id) === String(selectedEmployee))?.name || "";
      void logEmployeeActivity({
        targetEmployeeId: selectedEmployee ? Number(selectedEmployee) : null,
        targetEmployeeName: selectedEmployeeName || undefined,
        action: "Salary History Unlocked",
        module: "Salary",
        description: selectedEmployeeName
          ? `Unlocked salary history for ${selectedEmployeeName}.`
          : "Unlocked salary history.",
      });
      setIsUnlocked(true);
      setPin("");
      setPinError("");
      return;
    }
    setPinError("Invalid PIN");
  };

  const handleDelete = async (id) => {
    try {
      await client.delete(`/salary-history/${id}`);
      await fetchSalaryHistory(selectedEmployee);
      setDeleteConfirm(null);
    } catch (err) {
      console.error("Delete salary history error:", err);
      alert("Failed to delete salary history. Please try again.");
    }
  };

  const filteredHistory = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return salaryHistory.filter((row) => {
      if (!isWithinMonthSelection(row.created_at, selectedMonths)) return false;
      if (!query) return true;
      return [
        row.employee_name, row.salary_month, row.salary_year, row.action_type,
        row.remarks, row.base_salary, row.salary_amount, row.advance_amount,
        row.deduction_amount, row.net_pay, row.present_days, row.absent_days,
        row.payable_days, row.per_day_salary, row.salary_type,
      ].join(" ").toLowerCase().includes(query);
    });
  }, [selectedMonths, salaryHistory, searchQuery]);

  const salaryDetailRows = useMemo(() => {
    if (!selectedSalaryDetail) return [];

    return [
      ["Employee Name", selectedSalaryDetail.employee_name || "-"],
      ["Month", formatSalaryMonth(selectedSalaryDetail)],
      ["Base Salary", formatCurrency(selectedSalaryDetail.base_salary ?? 0)],
      ["Present Days", getPresentDays(selectedSalaryDetail)],
      ["Absent Days", getAbsentDays(selectedSalaryDetail)],
      ["Per Day Salary", formatCurrency(selectedSalaryDetail.per_day_salary ?? 0)],
      ["Final Payable Salary", formatCurrency(selectedSalaryDetail.net_pay ?? 0)],
      ["Salary Paid", formatCurrency(selectedSalaryDetail.salary_amount ?? 0)],
      ["Advance Taken", formatCurrency(selectedSalaryDetail.advance_amount ?? 0)],
      ["Deduction", formatCurrency(selectedSalaryDetail.deduction_amount ?? 0)],
      ["Payment Status", getPaymentStatus(selectedSalaryDetail)],
      ["Total Working Days", getTotalWorkingDays(selectedSalaryDetail)],
      ["Created Date", formatDate(selectedSalaryDetail.created_at)],
    ];
  }, [selectedSalaryDetail]);

  useEffect(() => {
    setHistoryPage(1);
  }, [selectedEmployee, searchQuery, selectedMonths]);

  const historyPageCount = Math.max(1, Math.ceil(filteredHistory.length / SALARY_HISTORY_PAGE_SIZE));
  const paginatedHistory = filteredHistory.slice(
    (historyPage - 1) * SALARY_HISTORY_PAGE_SIZE,
    historyPage * SALARY_HISTORY_PAGE_SIZE
  );

  useEffect(() => {
    if (historyPage > historyPageCount) setHistoryPage(historyPageCount);
  }, [historyPage, historyPageCount]);

  return (
    <div className="salary-history-page">
      <div className="salary-history-header">
        <h2>Salary History</h2>
      </div>

      {/* Filter Container */}
      <div className="salary-filter">
        <div className="salary-filter-group">
          <label>Select Employee</label>
          <select value={selectedEmployee} onChange={handleEmployeeChange}>
            <option value="">All Employees</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.name || emp.firstName}
              </option>
            ))}
          </select>
        </div>

        <div className="salary-filter-group">
          <label>Search</label>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search employee, month, status..."
          />
        </div>

        {/* Compact Dropdown Month Picker */}
        <div className="salary-filter-group salary-filter-months">
          <label>Select Month(s)</label>
          <div className="salary-month-picker" ref={monthPickerRef}>
            {/* Dropdown Trigger Button */}
            <button 
              type="button" 
              className="month-picker-trigger"
              onClick={() => setIsMonthPickerOpen(!isMonthPickerOpen)}
            >
              <FaCalendarAlt className="trigger-icon" />
              {selectedMonths.length === 0 
                ? "All Months" 
                : selectedMonths.length === 1 
                  ? availableMonths.find(m => m.value === selectedMonths[0])?.fullLabel 
                  : `${selectedMonths.length} months selected`
              }
              <FaChevronDown className={`chevron-icon ${isMonthPickerOpen ? "rotated" : ""}`} />
            </button>

            {/* Dropdown Popup */}
            {isMonthPickerOpen && (
              <div className="month-picker-dropdown">
                {/* Year Selector */}
                <div className="picker-year-selector">
                  <label>Year:</label>
                  <select 
                    value={pickerYear} 
                    onChange={(e) => setPickerYear(Number(e.target.value))}
                  >
                    {availableYears.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                
                {/* Month Grid */}
                <div className="picker-month-grid">
                  {currentYearMonths.map((month) => (
                    <button
                      key={month.value}
                      type="button"
                      className={`picker-month-btn 
                        ${month.selected ? "selected" : ""} 
                        ${!month.available ? "disabled" : ""}`}
                      onClick={() => {
                        if (month.available) {
                          toggleMonth(month.value);
                        }
                      }}
                      disabled={!month.available}
                    >
                      {month.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Selected Months Tags */}
            {selectedMonths.length > 0 && (
              <div className="selected-months">
                {selectedMonths.map((month) => {
                  const monthData = availableMonths.find((m) => m.value === month);
                  return (
                    <span key={month} className="selected-month-tag">
                      {monthData?.fullLabel}
                      <button
                        type="button"
                        onClick={() => toggleMonth(month)}
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
                <button
                  type="button"
                  className="clear-months-btn"
                  onClick={() => setSelectedMonths([])}
                >
                  Clear All
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* PIN Lock */}
      {!isUnlocked && (
        <div className="salary-history-lock-overlay">
          <div className="salary-history-lock-card">
            <h3>Protected Salary Data</h3>
            <p>Enter the 4-digit PIN to view salary history.</p>
            <form onSubmit={handleUnlock} className="salary-history-lock-form">
              <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                placeholder="Enter PIN"
              />
              <button type="submit" className="btn btn-primary">
                Unlock
              </button>
            </form>
            {pinError && <div className="salary-lock-error">{pinError}</div>}
          </div>
        </div>
      )}

      {isUnlocked && (
        <>
          {/* KPI Cards — gradient, no border, radius 0 */}
          <div className="summary-cards">
            <div className="summary-card">
              <div className="summary-card-icon-wrapper">
                <FaMoneyBillWave className="summary-card-icon" />
              </div>
              <div>
                <div className="summary-label">Total Payout</div>
                <div className="summary-value">{formatCurrency(summary.totalPayout)}</div>
                <div className="summary-trend"><FaArrowUp /> All time</div>
              </div>
            </div>

            <div className="summary-card">
              <div className="summary-card-icon-wrapper">
                <FaCheckCircle className="summary-card-icon" />
              </div>
              <div>
                <div className="summary-label">Total Paid</div>
                <div className="summary-value">{formatCurrency(summary.totalPaid)}</div>
                <div className="summary-trend">
                  <FaArrowUp /> {summary.paidPercent.toFixed(0)}% of total
                </div>
              </div>
            </div>

            <div className="summary-card">
              <div className="summary-card-icon-wrapper">
                <FaClock className="summary-card-icon" />
              </div>
              <div>
                <div className="summary-label">Total Pending</div>
                <div className="summary-value">{formatCurrency(summary.totalPending)}</div>
                <div className="summary-trend">
                  <FaArrowDown /> {summary.pendingPercent.toFixed(0)}% of total
                </div>
              </div>
            </div>

            <div className="summary-card">
              <div className="summary-card-icon-wrapper">
                <FaHandHoldingUsd className="summary-card-icon" />
              </div>
              <div>
                <div className="summary-label">Advance Given</div>
                <div className="summary-value">{formatCurrency(summary.totalAdvance)}</div>
                <div className="summary-trend"><FaArrowUp /> All time</div>
              </div>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <p className="salary-loading">Loading salary history...</p>
          ) : (
            <div className="salary-table-wrapper">
              <table className="salary-history-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Employee Name</th>
                    <th>Month</th>
                    <th>Base Salary</th>
                    <th>Salary Paid</th>
                    <th>Present</th>
                    <th>Absent</th>
                    <th>Payable</th>
                    <th>Per Day</th>
                    <th>Type</th>
                    <th>Advance</th>
                    <th>Deduction</th>
                    <th>Net Pay</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHistory.length === 0 ? (
                    <tr>
                      <td colSpan="15" className="salary-empty">
                        No history found
                      </td>
                    </tr>
                  ) : (
                    paginatedHistory.map((row, i) => (
                      <tr key={row.id || i}>
                        <td>{formatDate(row.created_at)}</td>
                        <td className="employee-name-cell">{row.employee_name || "-"}</td>
                        <td>{formatSalaryMonth(row)}</td>
                        <td className="amount-cell">{formatCurrency(row.base_salary ?? 0)}</td>
                        <td className="amount-cell">{formatCurrency(row.salary_amount ?? 0)}</td>
                        <td className="days-cell">{getPresentDays(row)}</td>
                        <td className="days-cell">{getAbsentDays(row)}</td>
                        <td className="days-cell">{row.payable_days ?? "-"}</td>
                        <td className="amount-cell">{formatCurrency(row.per_day_salary ?? 0)}</td>
                        <td>
                          {row.salary_type === "attendance_based" || row.salary_type === "daily"
                            ? "Attendance"
                            : row.salary_type
                            ? "Fixed"
                            : "-"}
                        </td>
                        <td className="amount-cell">{formatCurrency(row.advance_amount ?? 0)}</td>
                        <td className="amount-cell amount-cell-red">{formatCurrency(row.deduction_amount ?? 0)}</td>
                        <td className="amount-cell amount-cell-bold">{formatCurrency(row.net_pay ?? 0)}</td>
                        <td>
                          <span
                            className={`salary-status salary-status-${String(
                              row.action_type || ""
                            ).toLowerCase()}`}
                          >
                            {row.action_type || "-"}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="salary-history-view-btn"
                            onClick={() => setSelectedSalaryDetail(row)}
                          >
                            <FaEye />
                            View
                          </button>
                          <button
                            type="button"
                            className="salary-history-delete-btn"
                            onClick={() => setDeleteConfirm(row.id)}
                          >
                            <FaTrash />
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {filteredHistory.length > SALARY_HISTORY_PAGE_SIZE && (
            <div className="salary-history-pagination">
              <button
                type="button"
                className="salary-history-pagination-btn"
                disabled={historyPage === 1}
                onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </button>
              {Array.from({ length: historyPageCount }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  type="button"
                  className={`salary-history-pagination-btn${historyPage === page ? " active" : ""}`}
                  onClick={() => setHistoryPage(page)}
                >
                  {page}
                </button>
              ))}
              <button
                type="button"
                className="salary-history-pagination-btn"
                disabled={historyPage === historyPageCount}
                onClick={() => setHistoryPage((p) => Math.min(historyPageCount, p + 1))}
              >
                Next
              </button>
            </div>
          )}

          {selectedSalaryDetail && (
            <div className="salary-detail-modal-overlay" onClick={() => setSelectedSalaryDetail(null)}>
              <div className="salary-detail-modal" onClick={(event) => event.stopPropagation()}>
                <div className="salary-detail-modal-header">
                  <div>
                    <h3>Salary Details</h3>
                    <p>{selectedSalaryDetail.employee_name || "Employee"}</p>
                  </div>
                  <button
                    type="button"
                    className="salary-detail-close-btn"
                    onClick={() => setSelectedSalaryDetail(null)}
                    aria-label="Close salary details"
                  >
                    <FaTimes />
                  </button>
                </div>
                <div className="salary-detail-grid">
                  {salaryDetailRows.map(([label, value]) => (
                    <div key={label} className="salary-detail-item">
                      <span>{label}</span>
                      <strong>{value}</strong>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {deleteConfirm && (
            <div className="salary-detail-modal-overlay" onClick={() => setDeleteConfirm(null)}>
              <div className="salary-detail-modal" onClick={(event) => event.stopPropagation()}>
                <div className="salary-detail-modal-header">
                  <div>
                    <h3>Confirm Delete</h3>
                    <p>Are you sure you want to delete this salary record?</p>
                  </div>
                  <button
                    type="button"
                    className="salary-detail-close-btn"
                    onClick={() => setDeleteConfirm(null)}
                    aria-label="Cancel delete"
                  >
                    <FaTimes />
                  </button>
                </div>
                <div className="salary-detail-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setDeleteConfirm(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => handleDelete(deleteConfirm)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
